// src/online/online.ts
// Async share-link multiplayer controller.
// Holds the current match's action log, replays actions on URL load,
// and produces share URLs after each turn.

import type { GameState } from '../sim/state';
import { stepSim } from '../sim/state';
import { aimToRadians, aimToPowerFrac } from '../input/controls';
import { WEAPONS } from '../sim/weapons';
import { type MatchData, type MatchAction, encodeMatch, buildMatchUrl, MATCH_LINK_VERSION } from './match-link';

export interface CodeModeState {
  /** Lobby code from /api/match. 4–12 chars. */
  code: string;
  /** Which player slot this device controls (host=0, joiners=1..3). */
  myPlayer: number;
  /** Last server version we've seen / written. */
  serverVersion: number;
  /** Secret returned by POST /api/match action=create. Host-only; absent on joiners. */
  hostToken?: string;
}

export interface OnlineSession {
  active: boolean;
  matchData: MatchData;
  /** Are we mid-replay? Suppress sfx/particles when true. */
  replaying: boolean;
  /** Player index whose turn JUST ended (drives "send to next" overlay). */
  lastFinishedPlayer: number;
  /** Set true when current player has fired AND sim resolved → show overlay. */
  showShareOverlay: boolean;
  /** Non-null when playing via a code-based lobby (real-time over backend). */
  codeMode: CodeModeState | null;
}

export function newOnlineSession(): OnlineSession {
  return {
    active: false,
    matchData: {
      version: MATCH_LINK_VERSION,
      seed: 0,
      matchTarget: 3,
      playerCount: 2,
      names: [],
      actions: [],
    },
    replaying: false,
    lastFinishedPlayer: 0,
    showShareOverlay: false,
    codeMode: null,
  };
}

/** Begin a new online session from the given config (creator side). */
export function startNewOnlineMatch(
  online: OnlineSession,
  cfg: { seed: number; matchTarget: number; playerCount: number; names: string[] },
): void {
  online.active = true;
  online.matchData = {
    version: MATCH_LINK_VERSION,
    seed: cfg.seed,
    matchTarget: cfg.matchTarget,
    playerCount: cfg.playerCount,
    names: cfg.names.slice(0, cfg.playerCount),
    actions: [],
  };
  online.replaying = false;
  online.lastFinishedPlayer = 0;
  online.showShareOverlay = false;
  online.codeMode = null;
}

/** Initialize from a decoded match link (joining side). */
export function loadOnlineMatch(online: OnlineSession, m: MatchData): void {
  online.active = true;
  online.matchData = m;
  online.replaying = false;
  online.lastFinishedPlayer = 0;
  online.showShareOverlay = false;
  online.codeMode = null;
}

/**
 * Fast-forward replay: apply each action in the log to `state`, stepping the
 * sim until back to 'aiming'. Caller must set online.replaying=true around
 * this call to suppress sfx/particles.
 *
 * Also handles round-over transitions by invoking the supplied resetRound fn.
 */
export function replayActions(
  state: GameState,
  actions: MatchAction[],
  resetRound: (s: GameState) => void,
  giveStarterInventory: (s: GameState) => void,
): void {
  // Make sure starter inventory is applied (creator side already did it,
  // but on URL load we just built fresh state).
  giveStarterInventory(state);

  const SDT = 1 / 60;
  const MAX_TICKS_PER_ACTION = 1200; // 20s safety cap

  for (const action of actions) {
    // If we ended the previous action in round-over, start a new round
    if (state.turn.phase === 'round-over') {
      resetRound(state);
    }
    if ((state.turn.phase as string) !== 'aiming') {
      // Should be aiming now; step a few times to flush
      for (let i = 0; i < 60 && (state.turn.phase as string) !== 'aiming'; i++) {
        stepSim(state, SDT);
      }
    }
    const cp = state.turn.currentPlayer;
    const tank = state.tanks[cp];
    const weapon = WEAPONS[action.weaponIdx];
    if (weapon && tank) {
      // Decrement ammo (matches live-fire path semantics)
      if (!weapon.isFree) {
        const qty = tank.inventory[weapon.id] ?? 0;
        if (qty > 0) tank.inventory[weapon.id] = qty - 1;
      }
      tank.selectedWeapon = weapon.id;
    }
    state.pendingFire = {
      angle: aimToRadians(action.angleDeg),
      power: aimToPowerFrac(action.power),
    };
    // Step at least once to consume pendingFire (which transitions aiming→flying),
    // then keep stepping until back to aiming (or round-over, or stuck).
    // A plain `while (phase !== 'aiming')` exits immediately — we START in aiming.
    let ticks = 0;
    do {
      stepSim(state, SDT);
      ticks++;
    } while (
      ticks < MAX_TICKS_PER_ACTION &&
      state.turn.phase !== 'aiming' &&
      state.turn.phase !== 'round-over'
    );
    // If sim just transitioned from flying→resolving but pendingFire was
    // never consumed (turn was wrong somehow), bail.
    if (state.pendingFire) state.pendingFire = null;
  }

  // Final round-over check after last action
  if (state.turn.phase === 'round-over') {
    // Leave it; main loop will award win & start next round on next user click.
    // For online, we auto-handle in commitFiredAction below.
  }
}

/** Append an action to the log. */
export function recordAction(online: OnlineSession, a: MatchAction): void {
  if (!online.active) return;
  online.matchData.actions.push(a);
}

/** Build the shareable URL for the current state of the match. */
export function currentShareUrl(online: OnlineSession): string {
  return buildMatchUrl(encodeMatch(online.matchData));
}

/** Display label of the player whose turn comes next (after current). */
export function nextPlayerLabel(online: OnlineSession, currentPlayer: number, alive: boolean[]): string {
  const n = online.matchData.playerCount;
  if (n <= 0) return '';
  let next = (currentPlayer + 1) % n;
  for (let i = 0; i < n; i++) {
    if (alive[next]) break;
    next = (next + 1) % n;
  }
  return online.matchData.names[next] ?? `Player ${next + 1}`;
}
